import React, { useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import { ProfileType } from './types/enums';
import MainLayout from './components/MainLayout';
import FormLayout from './forms/FormLayout';
import { RequireRole } from './components/RequireRole';
import DashboardOrPlaceholder from './pages/DashboardOrPlaceholder';
import PlaceholderPage from './pages/PlaceholderPage';
import Services from './pages/Services/Services';
// Wave 0.5 item 6 — staff Promotions + Customers pages are hidden for the
// pilot (routes neutralized below). Imports removed; the page files are
// preserved under src/pages/Promotions and src/pages/Customers.
import Profile from './pages/Settings/Profile/Profile';
// S2 (AB#2205, SG-07) — staff My Schedule (read-only).
import MySchedule from './pages/Staff/MySchedule/MySchedule';
import OwnerHelp from './pages/Owner/Help/Help';
import SupportPage from './pages/Support/SupportPage';
import OwnerAppointments from './pages/Owner/Appointments/Appointments';
// AB#1797 (W1) — feature-flagged new list view, gated below in AppointmentsOrPlaceholder.
import AppointmentsListPage from './pages/Owner/Appointments/AppointmentsListPage';
// Wave 0.5 item 6 — owner Promotions + Packages hidden for the pilot (routes
// neutralized below). Imports removed; page files preserved under
// src/pages/Owner/Promotion and src/pages/Owner/Packages.
import OwnerSettings from './pages/Owner/Settings/Settings';
// AB#1675 — My Business onboarding shell (replaces forced 5-step wizard with
// dashboard-first exploration + per-section sub-nav).
import MyBusiness from './pages/Owner/MyBusiness/MyBusiness';
// AB#1749 — Calendar day-view (C3 of Calendar v1 Epic AB#1746).
import CalendarPage from './pages/Owner/Calendar/CalendarPage';
import OwnerAccountSettings from './pages/Owner/Settings/AccountSettings/AccountSettings';
// GL-10 (Go-Live Wave 0): the Owner UserRoles page is a pure local-state
// mock (Add = TODO, Edit = alert(), no API). Import removed and its route
// neutralized to a redirect so it is unreachable in the pilot build.
import OwnerPaymentSettings from './pages/Owner/Settings/PaymentSettings/PaymentSettings';
// Wave 0.5 item 6 — /bookings-preview (URL-only) and the Subscription
// management page are hidden for the pilot (routes neutralized below). Imports
// removed; page files preserved under src/pages/BookingsPreview and
// src/pages/Owner/Subscription.
import PaymentsPage from './pages/Owner/Payments/PaymentsPage';
// AB#1904 (W-1) — Stripe Checkout return pages. Both are
// unauth-tolerant — they handle the bounce-back-with-stale-token case
// internally (redirect to /form/login?returnTo=...). NOT wrapped in
// RequireRole because the owner may land here with an expired JWT.
import SubscriptionSuccessPage from './pages/Owner/Subscription/SuccessPage';
import SubscriptionCancelledPage from './pages/Owner/Subscription/CancelledPage';
import NoShowPolicyPage from './pages/Owner/Settings/NoShowPolicy/NoShowPolicyPage';
import NotificationPreferences from './pages/Owner/Settings/NotificationPreferences/NotificationPreferences';
// Wave 0.5 item 6 — Loyalty, Inventory and Referrals hidden for the pilot
// (routes neutralized below). Imports removed; page files preserved under
// src/pages/Owner/{Loyalty,Inventory,Referrals}.
import PendingApprovalsPage from './pages/DashboardSuperAdmin/PendingApprovals/PendingApprovalsPage';
import ApprovalDetailPage from './pages/DashboardSuperAdmin/PendingApprovals/ApprovalDetailPage';
import UsersPage from './pages/DashboardSuperAdmin/Users/UsersPage';
import OwnersPage from './pages/DashboardSuperAdmin/Owners/OwnersPage';
// Batch C (AB#2195) — Super Admin booking support (search + cancel).
import BookingSupportPage from './pages/DashboardSuperAdmin/Support/BookingSupportPage';
import FeatureFlagsPage from './pages/DashboardSuperAdmin/FeatureFlags/FeatureFlagsPage';
// AB#1818 — SuperAdmin marketplace Categories page
import CategoriesPage from './pages/DashboardSuperAdmin/Categories/CategoriesPage';
// Wave 0.5 item 6 — non-core admin financial/monetization surfaces (Gender
// Overrides, Pricing Config, Subscription Analytics, Payment Dashboard,
// Payouts, Financial Transactions, Financial Reports, Platform Promotions) are
// hidden for the pilot (routes neutralized below). Imports removed; page files
// preserved under src/pages/DashboardSuperAdmin.
// GL-10 (Go-Live Wave 0): AuditLog + API Keys backends return 404 (empty
// shims) and Sponsored Placements serves mock data by default. Imports
// removed and their routes neutralized to redirects for the pilot build.
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { PageErrorBoundary } from './components/error-boundary';
import BuildVersionBadge from './components/BuildVersionBadge/BuildVersionBadge';
// Wave 0.5 item 6 — Chat and the legacy step wizards (/wizard, /onboarding)
// are hidden for the pilot (routes neutralized below). Imports removed; the
// ChatPage, Wizard and OnboardingWizard components — and their
// BusinessProvider / LoadingProvider / BlockIfOnboarded helpers — are
// preserved in the tree. OnboardingProvider stays (it still wraps the app).
import { OnboardingProvider } from './contexts/OnboardingContext';
import RequireOnboarding from './components/RequireOnboarding/RequireOnboarding';
import LoginForm from './forms/LoginForm/LoginForm';
import ForgetPasswordForm from './forms/ForgetPasswordForm/ForgetPasswordForm';
import PartnerRequestForm from './forms/PartnerRequestForm/PartnerRequestForm';
import SignupForm from './forms/SignupForm/SignupForm';
import OwnerVerifyAccount from './pages/Owner/VerifyAccount/VerifyAccount';
import OwnerResetPassword from './pages/Owner/ResetPassword/ResetPassword';
import OwnerAcceptInvitation from './pages/Owner/AcceptInvitation/AcceptInvitation';
// AB#1612 Phase 1B — verify-email landing page; the link in the BE
// verification email points here: /verify-email?email=...&token=...
import VerifyEmail from './pages/Auth/VerifyEmail';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useAuthContext } from './contexts/AuthContext';
// AB#1753 — Calendar v1 C7 realtime subscriber (Epic AB#1746).
import CalendarRealtimeBridge from './components/CalendarRealtimeBridge/CalendarRealtimeBridge';
import './styles/global.css';

// AB#1453: added 'signup' for the OTP self-registration form.
type FormType = 'login' | 'forgetPassword' | 'partner' | 'signup';

const FormWrapper: React.FC = () => {
    const [transition, setTransition] = useState(false);
    const navigate = useNavigate();

    const toggleForm = (form: FormType) => {
        setTransition(true);
        setTimeout(() => {
            navigate(`/form/${form}`);
            setTransition(false);
        }, 300);
    };

    return (
        <FormLayout>
            <div className={`form-transition ${transition ? 'fade-out' : 'fade-in'}`}>
                <Routes>
                    <Route index element={<Navigate to="login" replace />} />
                    <Route path="login" element={<LoginForm toggleForm={toggleForm} />} />
                    <Route path="forgetPassword" element={<ForgetPasswordForm toggleForm={toggleForm} />} />
                    <Route path="partner" element={<PartnerRequestForm toggleForm={toggleForm} />} />
                    {/* AB#1453: OTP-only salon owner self-registration (PO-D-012). */}
                    <Route path="signup" element={<SignupForm toggleForm={toggleForm} />} />
                </Routes>
            </div>
        </FormLayout>
    );
};

// AB#1797 (W1) — Appointments List V2. GL-10 (Go-Live Wave 0): the previous
// `if (true)` UAT force-on (CR-2026-05-30) was a landmine that hid the real
// intent. UAT is signed off and List V2 is the appointments surface for the
// pilot, so the gate is now an explicit, named constant. Behavior is
// unchanged (List V2 stays ON for owners + staff); flip to false to fall back
// to the legacy per-role views.
const APPOINTMENTS_LIST_V2_PILOT = true;

const AppointmentsOrPlaceholder: React.FC = () => {
    const { isSalonOwner, isSalonStaff } = useAuthContext();
    // S1 (AB#2204) — staff always get List V2: the legacy staff page (client-side
    // lifecycle + reschedule) is retired; staff actions are backend-allowed only.
    if (isSalonStaff || (APPOINTMENTS_LIST_V2_PILOT && isSalonOwner)) {
        return <AppointmentsListPage />;
    }
    if (isSalonOwner) return <OwnerAppointments />;
    return <PlaceholderPage title="Appointments" />;
};

const ServicesOrPlaceholder: React.FC = () => {
    const { isSalonOwner, isSalonStaff } = useAuthContext();
    // O3 (SO-29) — the owner's one Services home is My Business › Services.
    if (isSalonOwner) return <Navigate to="/my-business/services" replace />;
    if (isSalonStaff) return <Services />;
    return <PlaceholderPage title="Services" />;
};

// Wave 0.5 item 6 — the PromotionsAlias component (#1326) that routed the
// plural /promotions URL to the canonical owner /promotion page was removed
// along with the hidden Promotions surfaces. The /promotions route now
// redirects to the dashboard for the pilot (see below).

const App: React.FC = () => {
    return (
        <QueryClientProvider client={queryClient}>
        <ToastProvider>
            <LanguageProvider>
            <AuthProvider>
                <OnboardingProvider>
                <UserProfileProvider>
                    <UserProvider>
                    <NotificationsProvider>
                {/* AB#1753 — Mounts a single SignalR connection to /hubs/calendar
                    for the duration of the authenticated session. The hook
                    short-circuits internally when isAuthenticated=false, so
                    mounting here (above Routes) is safe for public routes. */}
                <CalendarRealtimeBridge />
                <Routes>
                    {/* Public routes */}
                    <Route path="/verify-account" element={<OwnerVerifyAccount />} />
                    {/* AB#1612 Phase 1B — link from verification email lands here */}
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/reset-password" element={<FormLayout><OwnerResetPassword /></FormLayout>} />
                    <Route path="/staff/accept-invitation" element={<OwnerAcceptInvitation />} />
                    {/* AB#1453: /signup alias → canonical /form/signup (OTP self-registration). */}
                    <Route path="/signup" element={<Navigate to="/form/signup" replace />} />
                    <Route path="/form/*" element={<FormWrapper />} />

                    {/* Protected: all roles - dashboard */}
                    <Route
                        path="/"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner, ProfileType.SalonStaff, ProfileType.SuperAdmin]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <DashboardOrPlaceholder />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner, ProfileType.SalonStaff, ProfileType.SuperAdmin]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <DashboardOrPlaceholder />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />

                    {/* Owner + Staff */}
                    <Route
                        path="/appointments"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner, ProfileType.SalonStaff]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <AppointmentsOrPlaceholder />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />
                    {/* Wave 0.5 item 6 — /bookings-preview is a URL-only owner
                        surface not part of the pilot journey. Route neutralized
                        to a redirect; the BookingsPreview page is preserved. */}
                    <Route
                        path="/bookings-preview"
                        element={<Navigate to="/" replace />}
                    />
                    {/* AB#1749 — Owner calendar day-view (read-only). Booking
                        creation flow ships in C5; status state-machine UI in
                        a sibling of C4. */}
                    <Route
                        path="/calendar"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <CalendarPage />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/services"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner, ProfileType.SalonStaff]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <ServicesOrPlaceholder />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />

                    {/* Owner only */}
                    {/* AB#1734 follow-up — the legacy /employee page has been
                        replaced by My Business → Manage Staff. Anyone landing
                        on the old URL (bookmarks, stale sidebar link, deep
                        link in older emails) is redirected to the new home.
                        Kept as a redirect rather than a deletion so the
                        deep-link experience stays unbroken. */}
                    <Route
                        path="/employee"
                        element={<Navigate to="/my-business/staff" replace />}
                    />
                    {/* Wave 0.5 item 6 — owner Promotions + Packages are
                        non-core for the pilot. Routes neutralized to redirects;
                        OwnerPromotion / OwnerPackages pages are preserved. */}
                    <Route
                        path="/promotion"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/packages"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/settings"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <OwnerSettings />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />
                    {/* AB#1675 — My Business onboarding shell. NOT wrapped in
                        RequireOnboarding because this IS the destination during
                        onboarding (the owner reaches it from the sidebar entry
                        point or the profile-menu link). */}
                    <Route
                        path="/my-business/*"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <MainLayout>
                                    <MyBusiness />
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/settings/accountsettings"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <OwnerAccountSettings />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />
                    {/* O3 (SO-29) — Branches has one home, My Business › Branches; the old
                        Settings › Branches URL (and its Settings card) lands there. */}
                    <Route
                        path="/settings/branches"
                        element={<Navigate to="/my-business/branches" replace />}
                    />
                    {/* GL-10 — Owner UserRoles is a mock surface (no backend).
                        Route neutralized: any hand-typed /settings/userroles
                        URL redirects back to Settings for the pilot. */}
                    <Route
                        path="/settings/userroles"
                        element={<Navigate to="/settings" replace />}
                    />
                    <Route
                        path="/settings/paymentsettings"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <OwnerPaymentSettings />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/settings/notifications"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <NotificationPreferences />
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/help"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <MainLayout>
                                    <OwnerHelp />
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/support"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner, ProfileType.SalonStaff, ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <SupportPage />
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    {/* Wave 0.5 item 6 — the legacy /wizard and /onboarding
                        step-based wizards are superseded by the My Business
                        onboarding shell and are not part of the pilot. Routes
                        neutralized to redirects; the Wizard / OnboardingWizard
                        components are preserved. */}
                    <Route
                        path="/wizard"
                        element={<Navigate to="/my-business" replace />}
                    />
                    <Route
                        path="/onboarding"
                        element={<Navigate to="/my-business" replace />}
                    />

                    {/* Wave 0.5 item 6 — staff Promotions (/promotions) and
                        Customers (/customers) are non-core for the pilot. Routes
                        neutralized to redirects; the Promotions / CustomersPage
                        pages are preserved. */}
                    <Route
                        path="/promotions"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/customers"
                        element={<Navigate to="/" replace />}
                    />
                    {/* S2 (AB#2205, SG-07) — staff only: own weekly hours, approved
                        time off and salon closures (read-only). */}
                    <Route
                        path="/schedule"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonStaff]}>
                                <MainLayout>
                                    <MySchedule />
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/settings/profile"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner, ProfileType.SalonStaff, ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <Profile />
                                </MainLayout>
                            </RequireRole>
                        }
                    />

                    {/* Phase 5 — Owner Payments */}
                    <Route
                        path="/payments"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <PageErrorBoundary pageName="Payments">
                                        <PaymentsPage />
                                    </PageErrorBoundary>
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />

                    {/* Phase 6 — Subscription & No-Show Policy */}
                    {/* Wave 0.5 item 6 — founding salons are free during the
                        pilot, so the Subscription management surface is hidden.
                        Route neutralized to a redirect; SubscriptionPage is
                        preserved. The Stripe checkout return pages below
                        (/subscription/success, /subscription/cancelled) stay
                        reachable as unauth-tolerant deep-link handlers. */}
                    <Route
                        path="/subscription"
                        element={<Navigate to="/" replace />}
                    />
                    {/* AB#1904 (W-1) — Stripe Checkout return pages.
                        Unauth-tolerant: NOT wrapped in RequireRole because
                        the owner may bounce back from Stripe with a stale
                        JWT. The pages themselves handle the redirect to
                        /form/login?returnTo=... when isAuthenticated=false. */}
                    <Route
                        path="/subscription/success"
                        element={
                            <PageErrorBoundary pageName="Subscription Success">
                                <SubscriptionSuccessPage />
                            </PageErrorBoundary>
                        }
                    />
                    <Route
                        path="/subscription/cancelled"
                        element={
                            <PageErrorBoundary pageName="Subscription Cancelled">
                                <SubscriptionCancelledPage />
                            </PageErrorBoundary>
                        }
                    />
                    <Route
                        path="/settings/no-show-policy"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SalonOwner]}>
                                <RequireOnboarding>
                                <MainLayout>
                                    <PageErrorBoundary pageName="No-Show Policy">
                                        <NoShowPolicyPage />
                                    </PageErrorBoundary>
                                </MainLayout>
                                </RequireOnboarding>
                            </RequireRole>
                        }
                    />

                    {/* Phase 7 — Super Admin */}
                    {/* Wave 0.5 item 6 — non-core admin financial/monetization
                        surfaces are hidden for the pilot. Routes neutralized to
                        redirects; the SubscriptionAnalyticsPage /
                        PaymentDashboardPage pages are preserved. */}
                    <Route
                        path="/subscription-analytics"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/payment-dashboard"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/pending-approvals"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <ErrorBoundary label="Pending Approvals"><PendingApprovalsPage /></ErrorBoundary>
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/pending-approvals/:businessId"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <ErrorBoundary label="Approval Detail"><ApprovalDetailPage /></ErrorBoundary>
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/users"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <ErrorBoundary label="Users"><UsersPage /></ErrorBoundary>
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/owners"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <ErrorBoundary label="Salon Owners"><OwnersPage /></ErrorBoundary>
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/booking-support"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <ErrorBoundary label="Booking Support"><BookingSupportPage /></ErrorBoundary>
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    <Route
                        path="/feature-flags"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <ErrorBoundary label="Feature Flags"><FeatureFlagsPage /></ErrorBoundary>
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    {/* AB#1842 (PBI-F) — SuperAdmin gender-mismatch override queue.
                        Wave 0.5 item 6 — hidden for the pilot. Route neutralized
                        to a redirect; GenderOverridesPage is preserved. */}
                    <Route
                        path="/gender-overrides"
                        element={<Navigate to="/" replace />}
                    />
                    {/* AB#1818 — SuperAdmin marketplace Categories management.
                        Route follows the flat `/categories` convention used by
                        sibling SA pages (verified vs /feature-flags, /audit-log,
                        /pricing-config in §5.1). */}
                    <Route
                        path="/categories"
                        element={
                            <RequireRole allowedRoles={[ProfileType.SuperAdmin]}>
                                <MainLayout>
                                    <ErrorBoundary label="Categories"><CategoriesPage /></ErrorBoundary>
                                </MainLayout>
                            </RequireRole>
                        }
                    />
                    {/* GL-10 — Audit Log backend returns 404 (empty shim).
                        Route neutralized to a redirect for the pilot build. */}
                    <Route
                        path="/audit-log"
                        element={<Navigate to="/" replace />}
                    />
                    {/* Wave 0.5 item 6 — Pricing Config hidden for the pilot.
                        Route neutralized to a redirect; PricingConfigPage is
                        preserved. */}
                    <Route
                        path="/pricing-config"
                        element={<Navigate to="/" replace />}
                    />
                    {/* GL-10 — API Keys backend returns 404 (empty shim).
                        Route neutralized to a redirect for the pilot build. */}
                    <Route
                        path="/api-keys"
                        element={<Navigate to="/" replace />}
                    />

                    {/* AB#1732 — Sponsored Placement CMS (FU-5).
                        GL-10 — serves mock data by default (real endpoint not
                        shipped). Route neutralized to a redirect for the pilot. */}
                    <Route
                        path="/sponsored-placements"
                        element={<Navigate to="/" replace />}
                    />

                    {/* Platform Payouts (#1028) */}
                    {/* Wave 0.5 item 6 — Payouts, Financial Transactions,
                        Financial Reports and Platform Promotions are non-core
                        financial/monetization admin surfaces hidden for the
                        pilot. Routes neutralized to redirects; the PayoutsPage /
                        FinancialTransactionsPage / FinancialReportsPage /
                        PlatformPromotionsPage pages are preserved. */}
                    <Route
                        path="/payouts"
                        element={<Navigate to="/" replace />}
                    />

                    {/* Financial Transactions & Reports (#849, #850, #1003) */}
                    <Route
                        path="/financial-transactions"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/financial-reports"
                        element={<Navigate to="/" replace />}
                    />

                    {/* AB#1006 — Platform Promotions (SuperAdmin platform-scoped) */}
                    <Route
                        path="/platform-promotions"
                        element={<Navigate to="/" replace />}
                    />

                    {/* Phase 8 — Chat (all roles) */}
                    {/* Wave 0.5 item 6 — Chat is non-core for the pilot. Route
                        neutralized to a redirect; ChatPage is preserved. */}
                    <Route
                        path="/chat"
                        element={<Navigate to="/" replace />}
                    />

                    {/* Phase 9 — Loyalty & Inventory */}
                    {/* Wave 0.5 item 6 — Loyalty, Inventory and Referrals are
                        non-core owner surfaces hidden for the pilot. Routes
                        neutralized to redirects; the LoyaltyPage / InventoryPage
                        / ReferralsPage pages are preserved. */}
                    <Route
                        path="/loyalty"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/inventory"
                        element={<Navigate to="/" replace />}
                    />

                    {/* Phase 10 — API Keys & Referrals */}
                    <Route
                        path="/referrals"
                        element={<Navigate to="/" replace />}
                    />

                    {/* Catch-all: redirect to home (RequireRole on / will redirect to login if not authenticated) */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                    </NotificationsProvider>
                    </UserProvider>
                </UserProfileProvider>
                </OnboardingProvider>
            </AuthProvider>
            </LanguageProvider>
            <BuildVersionBadge />
        </ToastProvider>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    );
};

export default App;
